import React, {useState, useEffect, useRef, useMemo} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import {Text, Button, Icon, StatusBadge, TextInput, CurrencyInput, Switch} from '@/ui-kits';
import {appendPlural, formatName, getInitials} from '@/utils/common_functions';
import {StatusBadgeVariant, SpecialistProfileStatus, SpecialistOnboardingStatus} from '@/constants/enums';
import {ConfirmModal} from '@/components/common/confirmationModal';
import {FileViewerModal} from '@/components/common/FileViewerModal';
import {HospitalFeesModal} from '../hospital-admin/HospitalFeesModal';
import {PhotoGuidelinesTooltip} from './PhotoGuidelinesTooltip';
import {useDispatch, useSelector} from 'react-redux';
import {
  getSpecialistAdminProfileRequest,
  resetSpecialistAdminMessage,
  specialistAdminProfileRequestChangeRequest,
  setSpecialistAdminProfileFeeAndVisibilityRequest,
} from '@/services/redux/slices/specialistAdminSlice';
import {
  specialistAdminProfileSelector,
  specialistAdminDashboardStatus,
  specialistAdminDashboardFailureSelector,
  specialistAdminDashboardSuccessSelector,
} from '@/services/redux/selectors';
import {ErrorCodes, getErrorMessage} from '@/utils/getMessages';
import {useToast} from '@/hooks';
import {Routes} from '@/navigation/Routes';
import {SpecialistProfileToOnboardingStatusMapping} from '@/constants';

const changeRequestTags = ['Photo does not meet professional guidelines', 'Incomplete experience details', 'Clear signature required'];

// Validate and parse specialist ID from route params
const validateAndGetId = (id: string | undefined, showToast: (message: string, type: 'success' | 'failure') => void): number | null => {
  if (!id) {
    showToast('Specialist ID not provided', 'failure');
    return null;
  }
  const specialistId = parseInt(id, 10);
  if (isNaN(specialistId)) {
    showToast('Invalid specialist ID', 'failure');
    return null;
  }
  return specialistId;
};

// Build payload object for fee and visibility update API calls
const buildFeePayload = (id: number, fee: string, currency: number | null, visibility: boolean) => {
  const payload: {id: number; fee?: number; fee_currency?: number; is_visible?: boolean} = {id};
  if (fee) payload.fee = typeof fee === 'string' ? parseInt(fee, 10) : fee;
  if (currency) payload.fee_currency = currency;
  payload.is_visible = visibility;
  return payload;
};

export const SpecialistProfileDetailView: React.FC = () => {
  const {id} = useParams<{id: string}>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {showToast} = useToast();

  // ==========================
  // Redux Selectors
  // ==========================
  const profile = useSelector(specialistAdminProfileSelector);
  const isLoading = useSelector(specialistAdminDashboardStatus);
  const failure = useSelector(specialistAdminDashboardFailureSelector) as ErrorCodes;
  const success = useSelector(specialistAdminDashboardSuccessSelector);

  // ==========================
  // States
  // ==========================
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [changeRequestMessage, setChangeRequestMessage] = useState('');
  const [consultationFee, setConsultationFee] = useState<number | string>('');
  const [currency, setCurrency] = useState<number | null>(null);
  const [visibility, setVisibility] = useState(false);
  const [showVisibilityModal, setShowVisibilityModal] = useState(false);
  const [pendingVisibility, setPendingVisibility] = useState<boolean | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showPhotoGuidelines, setShowPhotoGuidelines] = useState(false);
  const [licenseViewerOpen, setLicenseViewerOpen] = useState(false);
  const [signatureImageError, setSignatureImageError] = useState(false);
  const hasFetchedRef = useRef<number | null>(null);
  const failureHandledRef = useRef<string | false>(false);
  const successHandledRef = useRef<string | false>(false);

  // Modal state for fee change
  const [showFeeModal, setShowFeeModal] = useState(false);
  const [pendingFee, setPendingFee] = useState<string | null>(null);
  const feeModalShownRef = useRef(false);

  const onboardingStatus = SpecialistProfileToOnboardingStatusMapping[profile?.profile_status?.id as keyof typeof SpecialistProfileToOnboardingStatusMapping];

  const isApproved = useMemo(() => onboardingStatus === SpecialistOnboardingStatus.Approved, [onboardingStatus]);
  const isChangeRequest = useMemo(() => onboardingStatus === SpecialistOnboardingStatus.ChangesRequested, [onboardingStatus]);

  const canApprove = useMemo(() => {
    return [SpecialistOnboardingStatus.UnderReview, SpecialistOnboardingStatus.ProfileEdited].includes(onboardingStatus);
  }, [onboardingStatus]);

  const isApproveDisabled = !consultationFee || consultationFee === 0 || !currency || isChangeRequest;
  const signatureUrl = profile?.specialist_signature ?? (profile as any)?.signature;

  // ==========================
  // useEffect Hooks
  // ==========================

  // Always start in read-only mode on mount/refresh
  useEffect(() => {
    setIsEditMode(false);
  }, [id]); // Reset when specialist ID changes (new profile loaded)

  // Fetch profile data when component mounts or ID changes
  useEffect(() => {
    const specialistId = validateAndGetId(id, showToast);
    if (!specialistId) {
      navigate(-1);
      return;
    }

    if (hasFetchedRef.current !== specialistId) {
      hasFetchedRef.current = null;
      failureHandledRef.current = false;
      successHandledRef.current = false;
      dispatch(resetSpecialistAdminMessage());
      // Reset edit mode when loading a new profile
      setIsEditMode(false);
    }

    if (hasFetchedRef.current === null) {
      hasFetchedRef.current = specialistId;
      dispatch(getSpecialistAdminProfileRequest({id: specialistId}));
    }
  }, [id, dispatch, navigate, showToast]);

  // Update local state when profile data is loaded
  useEffect(() => {
    if (profile) {
      setConsultationFee(profile.fee ?? '');
      setCurrency(profile.fee_currency || null);
      // Default to inactive (false) if profile doesn't have visibility set, otherwise use profile value
      setVisibility(profile.is_visible ?? false);
    }
  }, [profile]);

  useEffect(() => {
    setSignatureImageError(false);
  }, [signatureUrl]);

  // Handle error messages from API calls
  useEffect(() => {
    if (failure && failureHandledRef.current !== failure) {
      failureHandledRef.current = failure;
      showToast(getErrorMessage(failure), 'failure');
      dispatch(resetSpecialistAdminMessage());
    }
  }, [failure, showToast, dispatch]);

  // Handle success messages and navigate based on action type
  useEffect(() => {
    if (!success) return;
    const successString = String(success);

    if (successString === 'S-10108') {
      if (successHandledRef.current !== successString) {
        successHandledRef.current = successString;
        dispatch(resetSpecialistAdminMessage());
      }
      return;
    }

    // Only process action success codes if they haven't been handled yet
    if (successHandledRef.current !== successString) {
      successHandledRef.current = successString;
      dispatch(resetSpecialistAdminMessage());

      if (successString === 'S-10109') {
        setIsEditMode(false);
        navigate(Routes.SPECIALIST_ADMIN_ONBOARDING_SPECIALIST);
      } else if (successString === 'S-10110') {
        setChangeRequestMessage('');
        setSelectedTags([]);
        setTimeout(() => navigate(Routes.SPECIALIST_ADMIN_ONBOARDING_SPECIALIST), 500);
      }
    }
  }, [success, dispatch, navigate]);

  // ==========================
  // Event Handlers
  // ==========================

  // Toggle tag selection and update change request message
  const handleTagClick = (tag: string) => {
    setSelectedTags(prev => {
      const newTags = prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag];
      // Update textbox with selected tags
      if (newTags.length > 0) {
        setChangeRequestMessage(newTags.join('\n'));
      } else {
        setChangeRequestMessage('');
      }
      return newTags;
    });
  };

  const handleConsultationFeeBlur = () => {
    if (feeModalShownRef.current) return;
    const originalFee = profile?.fee ? String(profile.fee) : '';
    if (consultationFee !== originalFee && profile?.is_hospital_specialist) {
      setPendingFee(String(consultationFee));
      setShowFeeModal(true);
      feeModalShownRef.current = true;
    }
  };

  const handleFeeModalClose = () => {
    setShowFeeModal(false);
    setPendingFee(null);
    // Optionally reset the fee if not confirmed
    // setConsultationFee(profile?.fee ? String(profile.fee) : '');
  };

  const handleFeeModalConfirm = () => {
    setShowFeeModal(false);
    setPendingFee(null);
    // Keep the new fee as is
  };

  // Send change request to specialist
  const handleRequestChanges = () => {
    const specialistId = validateAndGetId(id, showToast);
    if (!specialistId) return;
    if (!changeRequestMessage.trim()) {
      return;
    }
    dispatch(specialistAdminProfileRequestChangeRequest({id: specialistId, message: changeRequestMessage.trim()}));
  };

  // Approve specialist profile with fee and visibility settings
  const handleApproveProfile = () => {
    const specialistId = validateAndGetId(id, showToast);
    if (!specialistId) return;
    if (!consultationFee || consultationFee === 0 || !currency) {
      showToast('Please set consultation fee and currency before approving', 'failure');
      return;
    }
    dispatch(setSpecialistAdminProfileFeeAndVisibilityRequest(buildFeePayload(specialistId, String(consultationFee), currency, visibility)));
  };

  // Enable edit mode for approved profiles
  const handleEdit = () => setIsEditMode(true);

  // Save fee and visibility changes for approved profiles
  const handleSave = () => {
    const specialistId = validateAndGetId(id, showToast);
    if (!specialistId) return;
    if (!consultationFee || consultationFee === 0 || !currency) {
      return;
    }
    dispatch(setSpecialistAdminProfileFeeAndVisibilityRequest(buildFeePayload(specialistId, String(consultationFee), currency, visibility)));
    setIsEditMode(false);
  };

  // Handle visibility toggle with confirmation modal
  const handleVisibilityToggle = (newValue: boolean) => {
    setPendingVisibility(newValue);
    setShowVisibilityModal(true);
  };

  // Confirm visibility change
  const handleConfirmVisibility = () => {
    if (pendingVisibility !== null) {
      setVisibility(pendingVisibility);
      setPendingVisibility(null);
    }
    setShowVisibilityModal(false);
  };

  // Cancel visibility change
  const handleCancelVisibility = () => {
    setPendingVisibility(null);
    setShowVisibilityModal(false);
  };

  if (isLoading && !profile) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Text variant="heading" className="text-ui_blue">
          Loading specialist profile...
        </Text>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Text variant="heading" className="text-ui_blue">
          No profile data available
        </Text>
      </div>
    );
  }

  return (
    <div className="gap-6 flex flex-col p-4 xl:p-6 w-full max-w-full overflow-x-hidden">
      {/* Contact Information */}
      <div className="bg-ui_bg_sky/20 rounded-xl border border-ui_input_border p-4 sm:p-5 md:p-6 w-full max-w-full overflow-x-hidden">
        <div className="flex flex-col md:flex-row gap-4 md:gap-6 w-full max-w-full">
          {/* Profile Image */}
          <div className="flex-shrink-0 w-full md:w-auto relative">
            <div
              className="w-40 h-40 sm:w-48 sm:h-48 md:w-52 md:h-52 mt-2 md:mr-6 rounded bg-profile_bg_pale_blue flex items-center justify-center overflow-hidden border border-ui_input_border mx-auto md:mx-0 cursor-pointer"
              onMouseEnter={() => setShowPhotoGuidelines(true)}
              onMouseLeave={() => setShowPhotoGuidelines(false)}>
              {profile.photo ? (
                <img src={profile.photo} alt={profile.name} className="w-full h-full object-cover" />
              ) : (
                <Text variant="free" className="text-ui_blue text-4xl sm:text-5xl font-FigtreeBold">
                  {getInitials(profile.name)}
                </Text>
              )}
            </div>

            <PhotoGuidelinesTooltip show={showPhotoGuidelines} />
          </div>

          {/* Contact Details */}
          <div className="flex-1 min-w-0 flex flex-col gap-3 w-full">
            <Text variant="subheading" className="!text-deep_blue mb-2 text-center md:text-left">
              Contact Information
            </Text>
            {/* Name and Phone in one line */}
            <div className="flex flex-col lg:flex-row items-start gap-4 lg:gap-8 xl:gap-12 mb-2">
              <div className="flex flex-col gap-1 w-full lg:w-auto lg:flex-1">
                <Text variant="body" className="text-ui_input_border_dark !font-FigtreeMeduim">
                  Name:
                </Text>
                <Text variant="body" className="text-deep_blue !font-FigtreeBold text-lg sm:text-xl md:!text-2xl break-words">
                  Dr. {formatName(profile.name)}
                </Text>
              </div>
              <div className="flex flex-col gap-1 w-full lg:w-auto lg:flex-1">
                <Text variant="body" className="text-ui_input_border_dark !font-FigtreeMeduim">
                  Phone:
                </Text>
                <div className="flex items-center gap-2 flex-wrap">
                  <Text variant="body" className="text-deep_blue !font-FigtreeSemiBold text-base sm:!text-xl break-words">
                    {profile.phone}
                  </Text>
                  <StatusBadge variant="secondary" status={StatusBadgeVariant.GREEN} className="py-1 flex-shrink-0">
                    Verified
                  </StatusBadge>
                </div>
              </div>
            </div>
            {/* Email and Location in another line */}
            <div className="flex flex-col lg:flex-row items-start gap-4 lg:gap-8 xl:gap-12">
              <div className="flex flex-col gap-1 w-full lg:w-auto lg:flex-1">
                <Text variant="body" className="text-ui_input_border_dark !font-FigtreeMeduim">
                  Email:
                </Text>
                <div className="flex items-center gap-2 flex-wrap">
                  <Text variant="body" className="text-deep_blue !font-FigtreeSemiBold text-base sm:!text-xl break-all">
                    {profile.email}
                  </Text>
                  <StatusBadge variant="secondary" status={StatusBadgeVariant.GREEN} className="py-1 flex-shrink-0">
                    Verified
                  </StatusBadge>
                </div>
              </div>
              <div className="flex flex-col gap-1 w-full lg:w-auto lg:flex-1">
                <Text variant="body" className="text-ui_input_border_dark !font-FigtreeMeduim">
                  Location:
                </Text>
                {
                  profile.is_hospital_specialist ? (
                    <Text variant="body" className="text-deep_blue !font-FigtreeSemiBold text-base sm:!text-xl break-words">
                      {profile.hospital_name}, {profile.hospital_address}
                    </Text>
                  ) : (
                    <Text variant="body" className="text-deep_blue !font-FigtreeSemiBold text-base sm:!text-xl break-words">
                      {profile.location?.label || '--'}
                    </Text>
                  )
                }
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* All Professional Sections in One Card */}
      <div className="bg-white rounded-xl border border-ui_input_border p-4 sm:p-5 md:p-6 flex flex-col gap-4 md:gap-6 w-full max-w-full">
        {/* Professional Information */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Icon type="stethoscope2" className="w-5 h-5 sm:w-6 sm:h-6" />
            <Text variant="subheading" className="text-ui_blue ml-2 !font-FigtreeSemiBold !text-2xl">
              Professional Information
            </Text>
          </div>
          <div className="flex flex-col gap-3">
            {/* First line: Designation and Education */}
            <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-4 md:gap-6">
              <div className="flex-1 min-w-0 w-full sm:w-auto sm:flex-1">
                <Text variant="body" className="!text-ui_input_border_dark !font-FigtreeMeduim sm:ml-10 break-words">
                  Designation : <span className="text-deep_blue !font-FigtreeSemiBold !text-base sm:!text-lg">{profile.designation || '--'}</span>
                </Text>
              </div>
              <div className="flex-1 min-w-0 w-full sm:w-auto sm:flex-1">
                <Text variant="body" className="text-ui_input_border_dark !font-FigtreeMeduim sm:ml-10 break-words">
                  Education : <span className="text-deep_blue !font-FigtreeSemiBold !text-base sm:!text-lg">{profile.education || '--'}</span>
                </Text>
              </div>
            </div>
            {/* Second line: Specialization and Years of Experience */}
            <div className="flex flex-col lg:flex-row items-start gap-4 lg:gap-4 xl:gap-6">
              <div className="w-full lg:w-auto lg:flex-1">
                <Text variant="body" className="text-ui_input_border_dark !font-FigtreeMeduim sm:ml-10 break-words">
                  Specialization : <span className="text-deep_blue !font-FigtreeSemiBold !text-base sm:!text-lg">{profile.specialization?.label || '--'}</span>
                </Text>
              </div>
              <div className="w-full lg:w-auto lg:flex-1">
                <Text variant="body" className="text-ui_input_border_dark !font-FigtreeMeduim sm:ml-10 break-words">
                  Years of Experience :{' '}
                  <span className="text-deep_blue !font-FigtreeSemiBold !text-base sm:!text-lg">
                    {profile.years_of_experience || 0} {appendPlural('Year', profile.years_of_experience || 0)}
                  </span>
                </Text>
              </div>
            </div>

            {/* Third line: License No. */}
            <div className="flex flex-col items-start gap-2">
              <Text variant="body" className="text-ui_input_border_dark !font-FigtreeMeduim sm:ml-10 break-words">
                License No. : <span className="text-deep_blue !font-FigtreeSemiBold !text-base sm:!text-lg">{profile.license?.license_number || '--'}</span>
              </Text>
              {profile.license?.url && (
                <Button
                  title="View Document"
                  variant="secondary"
                  className="w-auto max-w-[150px] py-1 mt-1 sm:ml-10"
                  leftIcon="file_blue"
                  onClick={() => setLicenseViewerOpen(true)}
                />
              )}
            </div>
          </div>
        </div>

        {/* Professional Experience */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Icon type="business_center_logo" className="w-6 h-6" />
            <Text variant="subheading" className="text-ui_blue ml-2 !font-FigtreeSemiBold !text-2xl">
              Professional Experience
            </Text>
          </div>
          <div className="bg-ui_bg_sky rounded-lg p-3 sm:p-4 sm:ml-8 border border-ui_input_border">
            {profile.professional_experience ? (
              <Text variant="body" className="text-deep_blue !font-FigtreeMeduim break-words whitespace-pre-line">
                {profile.professional_experience}
              </Text>
            ) : (
              <Text variant="body" className="text-ui_input_border_dark">
                --
              </Text>
            )}
          </div>
        </div>

        {/* Qualification Details */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Icon type="graduation_icon" className="w-6 h-6" />
            <Text variant="subheading" className="text-ui_blue ml-2 !font-FigtreeSemiBold !text-2xl">
              Qualification Details
            </Text>
          </div>
          <div className="bg-ui_bg_sky rounded-lg p-3 sm:p-4 sm:ml-8 border border-ui_input_border">
            {profile.qualification ? (
              <Text variant="body" className="text-deep_blue !font-FigtreeMeduim break-words whitespace-pre-line">
                {profile.qualification}
              </Text>
            ) : (
              <Text variant="body" className="text-ui_input_border_dark">
                --
              </Text>
            )}
          </div>
        </div>

        {/* Awards & Achievements */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Icon type="awards_logo" className="w-6 h-6" />
            <Text variant="subheading" className="text-ui_blue ml-2 !font-FigtreeSemiBold !text-2xl">
              Awards & Achievements
            </Text>
          </div>
          <div className="bg-ui_bg_sky rounded-lg p-3 sm:p-4 sm:ml-8 border border-ui_input_border">
            <div className="flex flex-col gap-2">
              {profile.awards_and_achievements && profile.awards_and_achievements.length > 0 ? (
                profile.awards_and_achievements.map((award: string, index: number) => (
                  <Text key={index} variant="body" className="text-deep_blue !font-FigtreeMeduim break-words">
                    {award}
                  </Text>
                ))
              ) : (
                <Text variant="body" className="text-ui_input_border_dark">
                  --
                </Text>
              )}
            </div>
          </div>
        </div>

        {/* Areas of Expertise */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Icon type="expertise_logo" className="w-6 h-6" />
            <Text variant="subheading" className="text-ui_blue ml-2 !font-FigtreeSemiBold !text-2xl">
              Areas of Expertise
            </Text>
          </div>

          {profile.areas_of_expertise && profile.areas_of_expertise.length > 0 ? (
            <div className="flex flex-wrap gap-3">
              {profile.areas_of_expertise.map((expertise: string, index: number) => (
                <button
                  key={index}
                  className="px-3 sm:px-4 py-1.5 sm:py-2 ml-0 w-full sm:w-auto sm:ml-9 rounded-lg border transition-colors bg-ui_bg_sky text-deep_blue border-ui_input_border hover:bg-ui_bg_sky">
                  <Text variant="body" className="text-deep_blue !font-FigtreeMeduim text-sm sm:text-base break-words">
                    {expertise}
                  </Text>
                </button>
              ))}
            </div>
          ) : (
            <Text variant="body" className="text-ui_input_border_dark sm:ml-10">
              --
            </Text>
          )}
        </div>

        {/* Signature */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Icon type="signature" className="w-6 h-6" />
            <Text variant="subheading" className="text-ui_blue ml-2 !font-FigtreeSemiBold !text-2xl">
              Signature
            </Text>
          </div>
          {signatureUrl ? (
            <div className="relative w-full max-w-[200px] h-[120px] sm:h-[150px] overflow-hidden sm:ml-10">
              {!signatureImageError ? (
                <img src={signatureUrl} alt="Signature" className="w-full h-full object-contain" onError={() => setSignatureImageError(true)} />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center gap-2">
                  <Icon type="file" className="w-10 h-10" />
                  <Text variant="body" className="text-ui_input_border_dark">
                    Preview not available
                  </Text>
                </div>
              )}
            </div>
          ) : (
            <Text variant="body" className="text-ui_input_border_dark sm:ml-10">
              --
            </Text>
          )}
        </div>
      </div>

      {/* Consultation Fee and Change Request */}
      <div className="bg-ui_bg_sky/20 rounded-xl border border-ui_input_border p-4 sm:p-5 md:p-6 flex flex-col gap-4 md:gap-6 w-full max-w-full">
        {/* Consultation Fee */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Icon type="consultaion_fee" className="w-5 h-5 sm:w-6 sm:h-6" />
            <Text variant="subheading" className="text-ui_blue ml-2 !font-FigtreeSemiBold !text-2xl">
              Consultation Fee <span className="text-rejection">*</span>
            </Text>
          </div>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:ml-10">
              <CurrencyInput
                value={consultationFee === '' ? '' : String(consultationFee)}
                currency={currency}
                onChange={(amount, currencyId) => {
                  setConsultationFee(amount === '' ? '' : Number(amount));
                  setCurrency(currencyId);
                }}
                onBlur={handleConsultationFeeBlur}
                placeholder="Enter Consultation Fee"
                className="w-full sm:max-w-md bg-white"
                disabled={(isApproved ? !isEditMode : false) || isChangeRequest}
                lockCurrency={(isApproved ? !isEditMode : false) || isChangeRequest}
              />
              {/* Fee Change Modal */}
              {showFeeModal && profile?.is_hospital_specialist && (
                <HospitalFeesModal
                  open={showFeeModal}
                  onClose={handleFeeModalClose}
                  originalFee={profile?.fee ?? 0}
                  newFee={pendingFee ? Number(pendingFee) : 0}
                  onConfirm={handleFeeModalConfirm}
                  variant='specalist-onboard'
                />
              )}
              <Text
                variant="body"
                className="!text-ui_input_border_dark !font-FigtreeRegular text-sm sm:text-base sm:whitespace-nowrap break-words sm:break-normal">
                (Fee must be set before approval)
              </Text>
            </div>
            <div className="flex items-center gap-3 mt-4 sm:ml-10">
              <Text variant="subheading" className="text-ui_blue !font-FigtreeSemiBold !text-2xl">
                Set Visibility
              </Text>
              <div className="scale-125 origin-left">
                <Switch value={visibility} onChange={handleVisibilityToggle} disabled={(isApproved && !isEditMode) || isChangeRequest} />
              </div>
            </div>
          </div>
        </div>

        {/* Send Change Request - Only show when not approved */}
        {!isChangeRequest && (
          <div>
            <div className="flex items-center gap-2 mb-4 mt-4">
              <Icon type="change_request" className="w-6 h-6" />
              <Text variant="subheading" className="text-ui_blue ml-2 !font-FigtreeSemiBold !text-2xl">
                Send Change Request
              </Text>
            </div>
            <div className="flex flex-col gap-4">
              <div className="flex flex-wrap gap-2 sm:ml-10">
                {changeRequestTags.map((tag, index) => (
                  <button
                    key={index}
                    onClick={() => handleTagClick(tag)}
                    className={`px-3 py-1 rounded-full border transition-colors ${
                      selectedTags.includes(tag) ? 'bg-ui_blue text-white border-ui_blue' : 'bg-white text-deep_blue border-ui_input_border'
                    }`}>
                    <Text
                      variant="body"
                      className={
                        selectedTags.includes(tag)
                          ? 'text-white !font-FigtreeRegular text-sm sm:text-base'
                          : 'text-deep_blue !font-FigtreeRegular text-sm sm:text-base break-words'
                      }>
                      {tag}
                    </Text>
                  </button>
                ))}
              </div>
              <div className="flex flex-col gap-2 w-full max-w-full">
                <Text variant="subheading" className="text-ui_blue sm:ml-10 !font-FigtreeSemiBold text-lg sm:!text-xl">
                  Message to Specialist :
                </Text>
                <div className="sm:ml-10 overflow-x-hidden">
                  <TextInput
                    value={changeRequestMessage}
                    onChangeText={setChangeRequestMessage}
                    multiline
                    placeholder="Enter details about required changes..."
                    className="bg-white w-full max-w-full"
                    wrapperClassName="w-full max-w-full"
                    disabled={isChangeRequest}
                  />
                </div>
                <Text variant="body" className="text-ui_input_border_dark !font-FigtreeRegular sm:ml-10 text-sm sm:text-base break-words">
                  This message will be included in the change request email
                </Text>
              </div>
              <Button
                title="Request Changes"
                variant="secondary"
                onClick={handleRequestChanges}
                className="w-full sm:max-w-[200px] sm:ml-10 bg-white text-ui_disabled"
                disabled={selectedTags.length === 0 && !changeRequestMessage}
              />
            </div>
          </div>
        )}
      </div>

      {/* Approve Profile Button - Only show when not approved */}
      {canApprove && (
        <div className="flex justify-center">
          <Button
            title="Approve Profile"
            variant="primary"
            onClick={handleApproveProfile}
            className={`w-full max-w-[200px] rounded-lg ${
              isApproveDisabled ? '!bg-[#AFAFAF] hover:!bg-[#AFAFAF]/90 !shadow-none !from-[#AFAFAF] !to-[#AFAFAF]' : 'shadow-md'
            }`}
            disabled={isApproveDisabled}
          />
        </div>
      )}

      {/* Edit and Save Buttons - Only show when approved */}
      {isApproved && (
        <div className="flex flex-row gap-3 sm:gap-4 justify-center items-center">
          <Button
            title="Edit"
            variant="secondary"
            onClick={handleEdit}
            className="w-full sm:w-auto sm:min-w-[120px] bg-white border border-ui_input_border text-deep_blue"
          />
          <Button
            title="Save"
            variant="primary"
            onClick={handleSave}
            className={`w-full sm:w-auto sm:min-w-[120px] rounded-lg ${
              !isEditMode ? '!bg-[#AFAFAF] hover:!bg-[#AFAFAF]/90 !shadow-none !from-[#AFAFAF] !to-[#AFAFAF]' : 'shadow-md'
            }`}
            disabled={!isEditMode || !consultationFee || consultationFee === 0}
          />
        </div>
      )}

      {/* Visibility Confirmation Modal */}
      <ConfirmModal
        open={showVisibilityModal}
        onClose={handleCancelVisibility}
        onConfirm={handleConfirmVisibility}
        header="Do you want to change visibility status?"
        message="This will update the specialist's profile visibility across the platform."
        cancelButtonText="Cancel"
        confirmButtonText="Confirm"
        primaryCta="cancel"
      />

      {/* License Document Viewer Modal */}
      {profile.license?.url && (
        <FileViewerModal
          isOpen={licenseViewerOpen}
          onClose={() => setLicenseViewerOpen(false)}
          file={{
            name: `License_${profile.license.license_number || 'Document'}.pdf`,
            url: profile.license.url,
            id: 0,
          }}
          // showDownloadButton={true}
          isSecure={false}
        />
      )}
    </div>
  );
};
